import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Archive, Newspaper, MessageCircle, ChevronRight, Info } from 'lucide-react';
import { API_URL, MEDIA_URL, ARCHIVE_SECTIONS, slugify } from '../constants';

// ── Modular modal component (handles Details, History, GPS, Map, Link URL)
import HeroDetailModal from '../components/HeroDetailModal';

// ── Static fallback hero images (used when API returns no data)
// Each entry may include an optional `link_url` that turns the image into a
// clickable link to an internal archive page or an external resource.
const heroImages = [
  {
    id: 1,
    url: `${MEDIA_URL}/images/gbongan_image_1.jpg`,
    name: "Olufi's Palace",
    latitude: 7.483, longitude: 4.353,
    address: "Palace Road, Gbongan, Osun State",
    details: "The Royal Palace of the Olufi of Gbongan, the paramount ruler of the community.",
    history: "The Olufi's Palace has stood as the seat of royalty in Gbongan for centuries, serving as the administrative and ceremonial hub of the kingdom.",
    link_url: "",   // e.g. "/archive/history-places-leadership" or "https://en.wikipedia.org/..."
  },
  {
    id: 2,
    url: `${MEDIA_URL}/images/gbongan_image_2.jpg`,
    name: "The Town Hall",
    latitude: 7.482, longitude: 4.352,
    address: "Town Hall Road, Gbongan",
    details: "Gbongan's civic meeting ground used for major public gatherings and government functions.",
    history: "Built during the colonial era, the Town Hall has witnessed key political transitions and serves as the voice of the community.",
    link_url: "",
  },
  {
    id: 3,
    url: `${MEDIA_URL}/images/gbongan_image_3.jpg`,
    name: "Park and Garden",
    latitude: 7.481, longitude: 4.351,
    address: "Central Gbongan",
    details: "The town's recreational park and botanical garden open to residents.",
    history: "Developed as a green space initiative, the park offers a serene environment for leisure and community events.",
    link_url: "",
  },
  {
    id: 4,
    url: `${MEDIA_URL}/images/gbongan_image_4.jpg`,
    name: "Lagos/Ibadan Motor Park",
    latitude: 7.486, longitude: 4.348,
    address: "Motor Park Road, Gbongan",
    details: "Major transportation hub connecting Gbongan to Lagos, Ibadan, and beyond.",
    history: "One of the oldest transport parks in Osun State, facilitating commerce and migration of Gbongan indigenes.",
    link_url: "",
  },
  {
    id: 5,
    url: `${MEDIA_URL}/images/gbongan_image_5.jpg`,
    name: "Town's Central Mosque",
    latitude: 7.484, longitude: 4.354,
    address: "Mosque Road, Gbongan",
    details: "The grand central mosque serving thousands of Muslim faithful in Gbongan.",
    history: "Founded in the 19th century, the Central Mosque reflects the deep Islamic heritage of a significant part of Gbongan's population.",
    link_url: "",
  },
  {
    id: 6,
    url: `${MEDIA_URL}/images/gbongan_image_6.jpg`,
    name: "Town Centre",
    latitude: 7.483, longitude: 4.352,
    address: "Gbongan Town Centre",
    details: "The bustling heart of Gbongan where commerce, culture, and community life converge.",
    history: "The town centre has always been the social nucleus of Gbongan, evolving from a traditional market square to a modern commercial zone.",
    link_url: "",
  },
  {
    id: 7,
    url: `${MEDIA_URL}/images/gbongan_image_7.jpg`,
    name: "The Cathedral Church",
    latitude: 7.485, longitude: 4.355,
    address: "Cathedral Road, Gbongan",
    details: "Historic Anglican cathedral that has served Gbongan's Christian community for over a century.",
    history: "Established by Christian missionaries in the early 1900s, the Cathedral stands as a symbol of faith and Western education in Gbongan.",
    link_url: "",
  },
  {
    id: 8,
    url: `${MEDIA_URL}/images/gbongan_image_8.JPG`,
    name: "Olufi at the Market",
    latitude: 7.485, longitude: 4.349,
    address: "Gbongan Central Market",
    details: "A rare glimpse of the Olufi engaging with traders and community members at the market.",
    history: "This photograph captures the tradition of royal visits to the market, reinforcing the bond between the palace and the people.",
    link_url: "",
  },
  {
    id: 9,
    url: `${MEDIA_URL}/images/gbongan_image_9.JPG`,
    name: "The Soldier's Statue",
    latitude: 7.480, longitude: 4.350,
    address: "Memorial Square, Gbongan",
    details: "A monument erected in honour of Gbongan's fallen soldiers who served in various wars.",
    history: "The statue commemorates brave indigenes who sacrificed their lives during the World Wars and subsequent Nigerian civil conflicts.",
    link_url: "",
  },
  {
    id: 10,
    url: `${MEDIA_URL}/images/gbongan_image_10.jpg`,
    name: "Townhall and Palace Complex",
    latitude: 7.483, longitude: 4.353,
    address: "Palace Road, Gbongan",
    details: "The administrative complex housing both the Town Hall and the adjoining palace grounds.",
    history: "This complex represents the dual governance of traditional and modern administration that has shaped Gbongan's development.",
    link_url: "",
  },
  {
    id: 11,
    url: `${MEDIA_URL}/images/gbongan_image_11.jpg`,
    name: "The Trumpet Bridge",
    latitude: 7.479, longitude: 4.357,
    address: "Bridge Road, Gbongan",
    details: "An iconic bridge at the edge of town, a vital crossing point for the community.",
    history: "Built to ease transportation across the river, the Trumpet Bridge has become one of Gbongan's most recognised landmarks.",
    link_url: "",
  },
];

const sectionColors = [
  'from-emerald-600 to-emerald-800',
  'from-blue-600 to-blue-800',
  'from-purple-600 to-purple-800',
  'from-amber-600 to-amber-800',
  'from-rose-600 to-rose-800',
];

// ─────────────────────────────────────────────────────────────────────────────

const HomePage = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newsItems, setNewsItems]                 = useState([]);
  const [heroImagesAPI, setHeroImagesAPI]         = useState([]);
  const [selectedImage, setSelectedImage]         = useState(null);

  // Auto-advance carousel
  useEffect(() => {
    const imgs = heroImagesAPI.length > 0 ? heroImagesAPI : heroImages;
    const interval = setInterval(() => {
      setCurrentImageIndex(p => (p + 1) % imgs.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [heroImagesAPI]);

  // Fetch data from backend
  useEffect(() => {
    // News
    fetch(`${API_URL}/news/recent/?limit=3`)
      .then(r => r.json())
      .then(d => setNewsItems(Array.isArray(d) ? d : []))
      .catch(() => {});

    // Hero images (API includes link_url field from backend)
    fetch(`${API_URL}/hero-images/`)
      .then(r => r.json())
      .then(d => {
        const arr = Array.isArray(d) ? d : (d.results || []);
        if (arr.length > 0) {
          setHeroImagesAPI(arr.map(img => ({
            ...img,
            url:     img.image?.startsWith('http') ? img.image : `${MEDIA_URL}/${img.image}`,
            name:    img.title,
            details: img.description,
            history: img.story,
            link_url: img.link_url || '',   // ← passed through to modal
          })));
        }
      })
      .catch(() => {});
  }, []);

  const displayImages = heroImagesAPI.length > 0 ? heroImagesAPI : heroImages;

  const defaultNews = [
    { id:1, title:'Community Festival 2026', date:'2026-01-25', content:'Annual cultural celebration bringing the community together.' },
    { id:2, title:'New Heritage Center Opens', date:'2026-01-20', content:'A new center dedicated to preserving Gbongan heritage.' },
    { id:3, title:'Youth Cultural Program',  date:'2026-01-15', content:'Youth programs focused on cultural education and preservation.' },
  ];
  const displayNews  = newsItems.length > 0 ? newsItems : defaultNews;
  const newsColors   = ['bg-rose-100', 'bg-teal-100', 'bg-violet-100'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
      {/* Audio is handled globally in App.js */}

      {/* ── Hero Carousel ───────────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">

          {/* Left: intro text */}
          <div className="p-8 flex flex-col justify-center">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Discover Our Community Legacy</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Explore important locations, historical figures, cultural landmarks, and stories that
              define the Gbongan community — a living archive connecting our glorious past to our
              promising future.
            </p>
            <p className="text-xs text-gray-400 mb-4 italic flex items-center gap-1">
              <Info className="w-3 h-3" /> Click on any image to view details
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/archive"
                className="bg-emerald-700 text-white px-5 py-2.5 rounded-lg hover:bg-emerald-800 transition flex items-center gap-2"
              >
                <Archive className="w-4 h-4" /> Browse Archive
              </Link>
              <Link
                to="/history-heritage"
                className="border-2 border-emerald-700 text-emerald-700 px-5 py-2.5 rounded-lg hover:bg-emerald-50 transition"
              >
                History & Heritage
              </Link>
            </div>
          </div>

          {/* Right: clickable carousel */}
          <div className="relative h-80 lg:h-auto min-h-[320px] overflow-hidden">
            <div
              className="flex h-full transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {displayImages.map(img => (
                <div
                  key={img.id}
                  className="min-w-full h-full relative cursor-pointer group"
                  onClick={() => setSelectedImage(img)}
                  title={`Click to learn about ${img.name}`}
                >
                  <img
                    src={img.url}
                    alt={img.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Gradient + labels */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between">
                    <div>
                      <span className="text-white font-semibold drop-shadow block">{img.name}</span>
                      {/* Small link indicator when link_url exists */}
                      {img.link_url && (
                        <span className="text-emerald-300 text-xs mt-0.5 flex items-center gap-1">
                          🔗 Has linked page
                        </span>
                      )}
                    </div>
                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition flex items-center gap-1">
                      <Info className="w-3 h-3" /> View Details
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Dot indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {displayImages.map((_, i) => (
                <button
                  key={i}
                  onClick={e => { e.stopPropagation(); setCurrentImageIndex(i); }}
                  className={`h-2 rounded-full transition-all ${i === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 w-2'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Archive Sections ────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Browse the Archive</h2>
            <p className="text-gray-600 text-sm mt-1">Five categories preserving our community's legacy</p>
          </div>
          <Link to="/archive" className="text-emerald-700 hover:text-emerald-800 text-sm font-medium flex items-center gap-1">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ARCHIVE_SECTIONS.map((s, i) => (
            <Link
              key={s.id}
              to={`/archive/${slugify(s.category)}`}
              className={`bg-gradient-to-br ${sectionColors[i]} text-white rounded-xl p-6 hover:scale-105 hover:shadow-xl transition-all duration-200`}
            >
              <div className="text-4xl mb-3">{s.icon}</div>
              <h3 className="font-bold text-lg mb-2 leading-tight">{s.category}</h3>
              <p className="text-white/80 text-sm leading-relaxed">{s.description}</p>
              <div className="mt-4 flex items-center gap-1 text-white/90 text-sm font-medium">
                Explore <ChevronRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Quick Links ─────────────────────────────────────────────────── */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { to: '/news',     icon: <Newspaper    className="w-8 h-8" />, title: 'Community News',   desc: 'Latest events and updates',        color: 'bg-blue-50   border-blue-200   text-blue-700'   },
          { to: '/chatroom', icon: <MessageCircle className="w-8 h-8" />, title: 'Global Chatroom', desc: 'Connect with community members',    color: 'bg-purple-50 border-purple-200 text-purple-700' },
        ].map(l => (
          <Link key={l.to} to={l.to} className={`${l.color} border-2 rounded-xl p-6 hover:shadow-md transition flex items-start gap-4`}>
            {l.icon}
            <div>
              <h3 className="font-bold">{l.title}</h3>
              <p className="text-sm opacity-75">{l.desc}</p>
            </div>
          </Link>
        ))}
      </section>

      {/* ── Latest News ─────────────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-md p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Latest News</h2>
          <Link to="/news" className="text-emerald-700 hover:text-emerald-800 text-sm font-medium flex items-center gap-1">
            All News <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {displayNews.slice(0, 3).map((n, i) => (
            <Link
              to={`/news/${n.id}`}
              key={n.id}
              className={`${newsColors[i % newsColors.length]} rounded-xl p-5 hover:shadow-md transition`}
            >
              <h3 className="font-semibold text-gray-800 mb-2">{n.title}</h3>
              <p className="text-xs text-gray-500 mb-2">
                {n.date ? new Date(n.date).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }) : ''}
              </p>
              {n.content && <p className="text-sm text-gray-600 line-clamp-2">{n.content}</p>}
            </Link>
          ))}
        </div>
      </section>

      {/* ── Hero Detail Modal (modular component) ───────────────────────── */}
      {selectedImage && (
        <HeroDetailModal
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
};

export default HomePage;
